import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

// Icons
import {
  UserIcon,
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  HomeIcon,
} from "@heroicons/react/24/outline";

// ✅ Correct Service Import from your centralized api.js
import { registerSuperAdmin } from "../services/api";

export default function Register() {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [passwordValidation, setPasswordValidation] = useState({
    hasMinLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  const notify = (msg, type) => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const handle = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const { password } = formData;
    if (password) {
      setPasswordValidation({
        hasMinLength: password.length >= 8,
        hasUppercase: /[A-Z]/.test(password),
        hasLowercase: /[a-z]/.test(password),
        hasNumber: /[0-9]/.test(password),
        hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      });
    } else {
      setPasswordValidation({
        hasMinLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecialChar: false,
      });
    }
  }, [formData.password]);

  const isPasswordValid =
    passwordValidation.hasMinLength &&
    passwordValidation.hasUppercase &&
    passwordValidation.hasLowercase &&
    passwordValidation.hasNumber &&
    passwordValidation.hasSpecialChar;

 const handleRegister = async () => {
    setIsLoading(true);
    try {
      const payload = {
        full_name: formData.fullName,
        email: formData.email,
        password: formData.password,
        confirm_password: formData.confirmPassword
      };

      const response = await registerSuperAdmin(payload);
      notify("Registered Successfully", "success");
    } catch (err) {
      // 500 error vachinappudu backend em response isthundo console lo chudu
      console.log("Full Error Object:", err.response);
      notify(err.response?.data?.detail || "Server Error: Check DB Connection", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid md:grid-cols-2 bg-white" style={{ fontFamily: "Poppins, sans-serif" }}>
      {/* LEFT SIDE */}
      <div className="hidden md:flex flex-col justify-center px-6 md:px-16 bg-gradient-to-br from-orange-400 to-orange-600 text-white">
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl md:text-4xl font-bold leading-snug">
          Admin Portal <br /> Access Control.
        </motion.h1>
        <p className="text-sm md:text-base opacity-90 mt-4 leading-relaxed">
          Manage your workforce with a powerful, secure, and centralized HR dashboard.
        </p>
        <div className="mt-8 space-y-4 text-sm">
          {["Full Administrative Control", "Secure Data Management", "Organization-wide Overview"].map((t, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }} className="flex items-center gap-3">
              <ShieldCheckIcon className="w-5 h-5 md:w-6 md:h-6" />
              {t}
            </motion.div>
          ))}
        </div>
      </div>

      {/* RIGHT SIDE */}
      <div className="flex items-center justify-center p-4 sm:p-8 md:p-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Link to="/" className="flex items-center text-orange-600 hover:text-orange-700 text-sm font-medium mb-6">
            <HomeIcon className="w-4 h-4 mr-2" /> Back to Home
          </Link>

          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">Admin Registration</h2>
          <p className="text-sm text-gray-500 mt-1 mb-6">Create your administrator account.</p>

          <div className="space-y-4">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Full Name</label>
              <div className="relative">
                <UserIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input name="fullName" value={formData.fullName} onChange={handle} className="w-full border rounded-lg p-3 pl-10 text-sm focus:border-orange-500 outline-none transition-all" placeholder="Admin Name" required />
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-600 mb-1 block">Email</label>
              <div className="relative">
                <EnvelopeIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input name="email" type="email" value={formData.email} onChange={handle} className="w-full border rounded-lg p-3 pl-10 text-sm focus:border-orange-500 outline-none transition-all" placeholder="admin@company.com" required />
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-600 mb-1 block">Password</label>
              <div className="relative">
                <LockClosedIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handle} className="w-full border rounded-lg p-3 pl-10 pr-10 text-sm focus:border-orange-500 outline-none transition-all" placeholder="••••••••" required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                </button>
              </div>
              {formData.password && !isPasswordValid && (
                <div className="mt-2 text-[10px] grid grid-cols-2 gap-1">
                  <p className={passwordValidation.hasMinLength ? "text-green-600" : "text-red-500"}>● 8+ chars</p>
                  <p className={passwordValidation.hasUppercase ? "text-green-600" : "text-red-500"}>● Uppercase</p>
                  <p className={passwordValidation.hasLowercase ? "text-green-600" : "text-red-500"}>● Lowercase</p>
                  <p className={passwordValidation.hasNumber ? "text-green-600" : "text-red-500"}>● Number</p>
                  <p className={passwordValidation.hasSpecialChar ? "text-green-600" : "text-red-500"}>● Special Char</p>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs text-gray-600 mb-1 block">Confirm Password</label>
              <div className="relative">
                <LockClosedIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type={showConfirmPassword ? "text" : "password"} name="confirmPassword" value={formData.confirmPassword} onChange={handle} className="w-full border rounded-lg p-3 pl-10 text-sm focus:border-orange-500 outline-none transition-all" placeholder="••••••••" required />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showConfirmPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button onClick={handleRegister} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg p-3 mt-4 shadow-sm transition-all flex items-center justify-center min-h-[48px]" disabled={isLoading}>
              {isLoading ? <ArrowPathIcon className="w-5 h-5 animate-spin" /> : "Register as Admin"}
            </button>

            <p className="text-xs text-gray-500 text-center mt-4">
              Already an admin? <Link to="/login" className="text-orange-600 font-semibold hover:underline">Sign In</Link>
            </p>
          </div>
        </motion.div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`fixed bottom-4 right-4 z-50 px-6 py-3 rounded-lg text-white shadow-xl ${notification.type === "success" ? "bg-green-600" : "bg-red-500"}`}>
          {notification.msg}
        </div>
      )}
    </div>
  );
}